import { useState } from 'react'
import styled from 'styled-components'
import NumericInput from '../numeric/NumericInput'
import useTranslation from '../../hooks/useTranslation'

/**
 * dualInput — one numeric value that lives in one of two fields at a time
 * (debit/credit, gross/net, …), picked with a two-way switch instead of two
 * separate boxes. See client/src/model/components/cardViewData for usage.
 *
 * config: {
 *   type: 'dualInput',
 *   id, secondaryId,                       // the two field ids
 *   primaryLabel, secondaryLabel,          // switch button text (translation keys)
 *   label, placeholder,                    // label is rendered by FormElem itself
 *                                          // (same as every other field type) —
 *                                          // do not render it again in here
 *   variant, decimals,                     // passed straight to NumericInput
 *   defaultSide: {                         // which side to pre-select while both
 *     field,                               // sides are still empty — reads a
 *     primaryWhen, secondaryWhen           // sibling field via linkedFields/
 *   },                                     // lineLinkedFields (e.g. the selected
 *                                          // account's normalBalance)
 * }
 */

const Wrapper = styled.div`
    display: flex;
    align-items: stretch;
    gap: 0.5rem;
`

const SwitchWrapper = styled.div`
    display: flex;
    flex: 0 0 auto;
    background-color: ${({ theme }) => theme.form.background};
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 0.5rem;
    padding: 0.2rem;
    gap: 0.25rem;
`

const SwitchOption = styled.button`
    flex: 0 1 auto;
    padding: 0.3rem 0.6rem;
    border: none;
    background: transparent;
    color: ${({ theme, $isActive }) => $isActive ? theme.btn.font : theme.font.color};
    font-size: var(--font-XS);
    font-weight: 600;
    cursor: pointer;
    border-radius: 0.375rem;
    min-width: 2rem;

    ${({ $isActive, theme }) => $isActive && `
        background-color: ${theme.btn.primary};
    `}

    &:hover:not(:disabled) {
        ${({ $isActive, theme }) => !$isActive && `background-color: ${theme.dropdown.active};`}
    }
`

const InputWrapper = styled.div`
    flex: 1 1 auto;
`

const resolveDefaultSide = (defaultSide, linkedFields) => {
    if (!defaultSide?.field) return 'primary'
    const linkedValue = linkedFields?.[defaultSide.field]
    if (linkedValue === undefined || linkedValue === null) return 'primary'
    if (defaultSide.secondaryWhen !== undefined && linkedValue === defaultSide.secondaryWhen) return 'secondary'
    return 'primary'
}

const DualInput = ({
    name,
    secondaryName,
    primaryValue,
    secondaryValue,
    setValue,
    placeholder,
    primaryLabel,
    secondaryLabel,
    $variant,
    decimals,
    linkedFields,
    defaultSide,
    isRed,
    inputId,
    testId,
}) => {
    const { t } = useTranslation()

    // The active side is whichever field holds a value — something other than
    // this control can fill one (a picked product writes the net), and the
    // switch has to show it. Only while both are empty does the user's own
    // pick decide, and failing that the linked-field suggestion, so it stays
    // correct as the user picks a different account before typing an amount.
    const [manualSide, setManualSide] = useState(null)

    const hasPrimary = !!primaryValue
    const hasSecondary = !!secondaryValue
    const suggestedSide = resolveDefaultSide(defaultSide, linkedFields)
    const activeSide = hasSecondary ? 'secondary' : hasPrimary ? 'primary' : (manualSide ?? suggestedSide)

    // Typing pins the side, so clearing the box to retype it does not flip the
    // switch to the other side the moment both fields are empty.
    const handleInput = (...args) => {
        setManualSide(activeSide)
        setValue(...args)
    }

    const handleSwitch = (side) => {
        if (side === activeSide) return
        setManualSide(side)
        const currentValue = activeSide === 'primary' ? primaryValue : secondaryValue
        const toName = side === 'primary' ? name : secondaryName
        const fromName = side === 'primary' ? secondaryName : name
        setValue([
            { target: { name: toName, value: currentValue || 0 } },
            { target: { name: fromName, value: 0 } }
        ])
    }

    return (
        <>
            <Wrapper>
                <SwitchWrapper data-testid={testId ? `${testId}.switch` : undefined}>
                    <SwitchOption
                        type="button"
                        $isActive={activeSide === 'primary'}
                        onClick={() => handleSwitch('primary')}
                        data-testid={testId ? `${testId}.switch.primary` : undefined}
                    >
                        {t(primaryLabel)}
                    </SwitchOption>
                    <SwitchOption
                        type="button"
                        $isActive={activeSide === 'secondary'}
                        onClick={() => handleSwitch('secondary')}
                        data-testid={testId ? `${testId}.switch.secondary` : undefined}
                    >
                        {t(secondaryLabel)}
                    </SwitchOption>
                </SwitchWrapper>
                <InputWrapper>
                    <NumericInput
                        $variant={$variant}
                        name={activeSide === 'primary' ? name : secondaryName}
                        inputId={inputId}
                        value={activeSide === 'primary' ? primaryValue : secondaryValue}
                        decimals={decimals}
                        placeholder={t(placeholder)}
                        setValue={handleInput}
                        isRed={isRed}
                        testId={testId}
                    />
                </InputWrapper>
            </Wrapper>
        </>
    )
}

export default DualInput
